import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DeviceCategoryDto,
  DeviceListQuery,
  DeviceListResponse,
  DeviceStatus,
  DeviceType,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

export interface DeviceFilters {
  categoryId: string | null;
  deviceType: DeviceType | null;
  status: DeviceStatus | null;
  search: string;
  /**
   * Show devices marked hidden. Off by default — the point of hiding one is
   * that it is not on the board — but reachable from the filters, because a
   * device you cannot list is a device you cannot unhide.
   */
  showHidden: boolean;
}

export const EMPTY_FILTERS: DeviceFilters = {
  categoryId: null,
  deviceType: null,
  status: null,
  search: '',
  showHidden: false,
};

/**
 * How often the board re-reads device state.
 *
 * Still a poll, and SPEC §11 still wants sub-second updates over a WebSocket —
 * but no longer because there is nothing to push. The server now keeps a socket
 * open to every agent (`apps/api/src/realtime`) and writes `lastSeenAt` and
 * `agentMachineId` on hello and on every heartbeat, so the `isOffline` flag on
 * these rows is live data rather than a column nothing ever set.
 *
 * What is missing is the client half: `socket.io-client` is not a dependency of
 * this app, and taking one on is a separate decision from building the server.
 * Until then the board learns about a machine going quiet on the next poll,
 * which is within the 35s the API uses to call an agent offline anyway.
 */
const POLL_INTERVAL_MS = 10_000;

function toQueryString(filters: DeviceFilters): string {
  const params = new URLSearchParams();
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.deviceType) params.set('deviceType', filters.deviceType);
  // `status` is intentionally NOT sent: the board needs unfiltered counts for
  // the filter chips, so status filtering happens client-side over the full
  // result. Category/type/search do go to the server.
  if (filters.search.trim()) params.set('search', filters.search.trim());
  // Without this the only way back to a hidden device would be the database.
  if (filters.showHidden) params.set('includeHidden', 'true');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useDevices(filters: DeviceFilters) {
  const [data, setData] = useState<DeviceListResponse | null>(null);
  // When the snapshot arrived — what a countdown ticks FROM between polls.
  const [receivedAt, setReceivedAt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Keeps the latest filters available to the interval without re-creating it.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (options: { quiet?: boolean } = {}) => {
    if (!options.quiet) setLoading(true);
    try {
      const response = await api.get<DeviceListResponse>(
        `/devices${toQueryString(filtersRef.current)}`,
      );
      setData(response);
      setReceivedAt(Date.now());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, filters.categoryId, filters.deviceType, filters.search, filters.showHidden]);

  useEffect(() => {
    // `quiet` so the board does not flash a spinner every 10 seconds.
    const timer = setInterval(() => void load({ quiet: true }), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { data, receivedAt, loading, error, reload: load };
}

export function useDeviceCategories() {
  const [categories, setCategories] = useState<DeviceCategoryDto[]>([]);

  const load = useCallback(async () => {
    try {
      setCategories(await api.get<DeviceCategoryDto[]>('/device-categories'));
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { categories, reload: load };
}

export type { DeviceListQuery };
